import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';
import { useTreatmentPathwayOptions } from '../useTreatmentPathwayOptions';

/**
 * TreatmentRecommendationsEformTab
 *
 * Handles Treatment & Recommendations section for Network Eform reports
 * Maps to NPDetails.eForm table fields
 *
 * Report types: 2010 (Network Eform Initial), 2011 (Network Eform Follow Up)
 * Section: psych-eform-treatment-recommendations
 */

// Hardcoded dropdown options (matching AngularJS implementation)
const REPORT_RECOMMENDATION_OPTIONS = [
  { value: 'T', label: 'Treatment' },
  { value: 'D', label: 'Discharge' }
];

const DISCHARGE_REASON_OPTIONS = [
  { value: 2108, label: 'Treatment complete - Referred for assessment only' },
  { value: 2109, label: 'Treatment complete - No treatment recommended following assessment' },
  { value: 2110, label: 'Treatment complete - Patient to continue with self-management' },
  { value: 2112, label: 'Treatment complete - Contract provision threshold reached' },
  { value: 2113, label: 'Treatment not complete - Patient failed to attend for treatment' },
  { value: 2114, label: 'Treatment not complete - Patient chose not to continue treatment' },
  { value: 2115, label: 'Treatment not complete - Change in patient circumstances' },
  { value: 2116, label: 'Treatment not complete - No contact/treatment after CV19' },
  { value: 2123, label: 'Treatment complete - condition resolved' },
  { value: 2124, label: 'Treatment complete - Recommend referral for other services' }
];

const RECOMMENDATION1_OPTIONS = [
  { value: 'a0', label: 'Onwards referral to GP support' },
  { value: 'a1', label: 'Onwards referral to psychiatric support' },
  { value: 'a2', label: 'Onwards referral to crisis support' },
  { value: 'a3', label: 'Occupational Health review' }
];

const TreatmentRecommendationsEformTab = ({ reportId, api, onSave, patientInfo , isPublished}) => {
  // Determine report type from patientInfo or API
  const [reportType, setReportType] = useState(null);

  // Form state - common fields
  const [reportRecommendation, setReportRecommendation] = useState('');
  const [treatmentPathway, setTreatmentPathway] = useState('');
  const [recommendedNumberOfSessions, setRecommendedNumberOfSessions] = useState('');
  const [treatmentPlanRecommended, setTreatmentPlanRecommended] = useState('');
  const [dischargeReasonID, setDischargeReasonID] = useState('');
  const [furtherDetails, setFurtherDetails] = useState('');
  const [recommendation1, setRecommendation1] = useState('');

  // Form state - Follow Up specific fields
  const [briefHistory, setBriefHistory] = useState('');
  const [currentlyOnMedicationForMHConditions, setCurrentlyOnMedicationForMHConditions] = useState(null);
  const [isChronicCondition, setIsChronicCondition] = useState(null);
  const [chronicConditionStartDate, setChronicConditionStartDate] = useState('');
  const [improvement, setImprovement] = useState('');
  const [patientPresentation, setPatientPresentation] = useState('');
  const [therapySessionsSummary, setTherapySessionsSummary] = useState('');
  const [furtherSessionReasons, setFurtherSessionReasons] = useState('');
  const [riskAssessment, setRiskAssessment] = useState('');
  const [rationaleForExtension, setRationaleForExtension] = useState('');
  const [willRecommendedSessionsEnableRecovery, setWillRecommendedSessionsEnableRecovery] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  // Use hardcoded dropdown options
  const reportRecommendationOptions = REPORT_RECOMMENDATION_OPTIONS;
  const { options: treatmentPathwayOptions } = useTreatmentPathwayOptions(
    api,
    reportId,
    patientInfo?.isCYPApplicable
  );
  const dischargeReasonOptions = DISCHARGE_REASON_OPTIONS;
  const recommendation1Options = RECOMMENDATION1_OPTIONS;

  // TODO: Get improvementOptions from API or hardcode them
  const improvementOptions = [];

  const isInitial = reportType === 2010;
  const isFollowUp = reportType === 2011;

  useEffect(() => {
    loadTreatmentRecommendationsData();
  }, [reportId]);

  const dateOnlyToString = (dateOnly) => {
    if (!dateOnly || !dateOnly.year) return '';
    const year = dateOnly.year;
    const month = String(dateOnly.month).padStart(2, '0');
    const day = String(dateOnly.day).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const stringToDateOnly = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10),
      day: parseInt(parts[2], 10)
    };
  };

  const loadTreatmentRecommendationsData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-eform-treatment-recommendations`);

      if (response.ok) {
        const data = await response.json();

        // Determine report type
        setReportType(data.reportTypeId || 2010);

        // Common fields
        setReportRecommendation(data.reportRecommendation || '');
        setTreatmentPathway(data.treatmentPathway !== null ? data.treatmentPathway : '');
        setRecommendedNumberOfSessions(data.recommendedNumberOfSessions !== null ? data.recommendedNumberOfSessions : '');
        setTreatmentPlanRecommended(data.treatmentPlanRecommended || '');
        setDischargeReasonID(data.dischargeReasonID !== null ? data.dischargeReasonID : '');
        setFurtherDetails(data.furtherDetails || '');
        setRecommendation1(data.recommendation1 || '');

        // Follow Up specific fields
        if (data.reportTypeId === 2011) {
          setBriefHistory(data.briefHistory || '');
          setCurrentlyOnMedicationForMHConditions(data.currentlyOnMedicationForMHConditions);
          setIsChronicCondition(data.isChronicCondition);
          setChronicConditionStartDate(dateOnlyToString(data.chronicConditionStartDate));
          setImprovement(data.improvement || '');
          setPatientPresentation(data.patientPresentation || '');
          setTherapySessionsSummary(data.therapySessionsSummary || '');
          setFurtherSessionReasons(data.furtherSessionReasons || '');
          setRiskAssessment(data.riskAssessment || '');
          setRationaleForExtension(data.rationaleForExtension || '');
          setWillRecommendedSessionsEnableRecovery(data.willRecommendedSessionsEnableRecovery);
        }

        // Dropdown options are now hardcoded constants (see top of file)

        // Set loaded fields as 'saved'
        const initialSavingStates = {};
        if (data.reportRecommendation) initialSavingStates['ReportRecommendation'] = 'saved';
        if (data.treatmentPathway !== null) initialSavingStates['TreatmentPathway'] = 'saved';
        if (data.recommendedNumberOfSessions !== null) initialSavingStates['RecommendedNumberOfSessions'] = 'saved';
        if (data.treatmentPlanRecommended) initialSavingStates['TreatmentPlanRecommended'] = 'saved';
        if (data.dischargeReasonID !== null) initialSavingStates['DischargeReasonID'] = 'saved';
        if (data.furtherDetails) initialSavingStates['FurtherDetails'] = 'saved';
        if (data.recommendation1) initialSavingStates['Recommendation1'] = 'saved';

        if (data.reportTypeId === 2011) {
          if (data.briefHistory) initialSavingStates['BriefHistory'] = 'saved';
          if (data.currentlyOnMedicationForMHConditions !== null) initialSavingStates['CurrentlyOnMedicationForMHConditions'] = 'saved';
          if (data.isChronicCondition !== null) initialSavingStates['IsChronicCondition'] = 'saved';
          if (data.chronicConditionStartDate) initialSavingStates['ChronicConditionStartDate'] = 'saved';
          if (data.improvement) initialSavingStates['Improvement'] = 'saved';
          if (data.patientPresentation) initialSavingStates['PatientPresentation'] = 'saved';
          if (data.therapySessionsSummary) initialSavingStates['TherapySessionsSummary'] = 'saved';
          if (data.furtherSessionReasons) initialSavingStates['FurtherSessionReasons'] = 'saved';
          if (data.riskAssessment) initialSavingStates['RiskAssessment'] = 'saved';
          if (data.rationaleForExtension) initialSavingStates['RationaleForExtension'] = 'saved';
          if (data.willRecommendedSessionsEnableRecovery !== null) initialSavingStates['WillRecommendedSessionsEnableRecovery'] = 'saved';
        }
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading treatment recommendations data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-eform-treatment-recommendations/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'ReportRecommendation': reportRecommendation,
      'TreatmentPathway': treatmentPathway,
      'RecommendedNumberOfSessions': recommendedNumberOfSessions,
      'TreatmentPlanRecommended': treatmentPlanRecommended,
      'DischargeReasonID': dischargeReasonID,
      'FurtherDetails': furtherDetails,
      'Recommendation1': recommendation1,
      'BriefHistory': briefHistory,
      'CurrentlyOnMedicationForMHConditions': currentlyOnMedicationForMHConditions,
      'IsChronicCondition': isChronicCondition,
      'ChronicConditionStartDate': chronicConditionStartDate,
      'Improvement': improvement,
      'PatientPresentation': patientPresentation,
      'TherapySessionsSummary': therapySessionsSummary,
      'FurtherSessionReasons': furtherSessionReasons,
      'RiskAssessment': riskAssessment,
      'RationaleForExtension': rationaleForExtension,
      'WillRecommendedSessionsEnableRecovery': willRecommendedSessionsEnableRecovery
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    saveField(fieldName, value);
  };

  const handleSelectChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleBooleanChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleDateChange = (fieldName, value, setter) => {
    setter(value);
    const dateOnlyValue = stringToDateOnly(value);
    saveField(fieldName, dateOnlyValue);
  };

  const handleNumberChange = (fieldName, value, setter) => {
    setter(value);
    const numValue = value === '' ? null : parseInt(value, 10);
    saveField(fieldName, numValue);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading treatment recommendations...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-section">
        <h3>Treatment & Recommendations</h3>

        <div className="form-group">
          <label className="inline-label">
            <strong>With this form, are you recommending: <span style={{ color: 'red' }}>*</span></strong>
            {getSaveIndicator('ReportRecommendation')}
          </label>
          <div className="radio-group">
            {reportRecommendationOptions.map(option => (
              <label key={option.value} style={{ display: 'block', marginBottom: '8px' }}>
                <input
                  type="radio"
                  name="reportRecommendation"
                  value={option.value}
                  checked={reportRecommendation === option.value}
                  onChange={() => handleSelectChange('ReportRecommendation', option.value, setReportRecommendation)}
                  style={{ marginRight: '8px' }}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        {isFollowUp && (
          <>
            <div className="form-group">
              <label htmlFor="briefHistory">
                Brief History: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('BriefHistory')}
              </label>
              <textarea
                id="briefHistory"
                value={briefHistory}
                onChange={(e) => handleFieldChange('BriefHistory', e.target.value, setBriefHistory)}
                onBlur={() => handleFieldBlur('BriefHistory', briefHistory)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group">
              <label className="inline-label">
                <strong>Is patient currently on medication for MH conditions? <span style={{ color: 'red' }}>*</span></strong>
                {getSaveIndicator('CurrentlyOnMedicationForMHConditions')}
              </label>
              <div className="radio-group-inline">
                <label>
                  <input
                    type="radio"
                    name="currentlyOnMedicationForMHConditions"
                    checked={currentlyOnMedicationForMHConditions === true}
                    onChange={() => handleBooleanChange('CurrentlyOnMedicationForMHConditions', true, setCurrentlyOnMedicationForMHConditions)}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="currentlyOnMedicationForMHConditions"
                    checked={currentlyOnMedicationForMHConditions === false}
                    onChange={() => handleBooleanChange('CurrentlyOnMedicationForMHConditions', false, setCurrentlyOnMedicationForMHConditions)}
                  />
                  No
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="inline-label">
                <strong>Is the condition chronic? <span style={{ color: 'red' }}>*</span></strong>
                {getSaveIndicator('IsChronicCondition')}
              </label>
              <div className="radio-group-inline">
                <label>
                  <input
                    type="radio"
                    name="isChronicCondition"
                    checked={isChronicCondition === true}
                    onChange={() => handleBooleanChange('IsChronicCondition', true, setIsChronicCondition)}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="isChronicCondition"
                    checked={isChronicCondition === false}
                    onChange={() => handleBooleanChange('IsChronicCondition', false, setIsChronicCondition)}
                  />
                  No
                </label>
              </div>
            </div>

            {isChronicCondition === true && (
              <div className="form-group">
                <label htmlFor="chronicConditionStartDate">
                  If yes, what is the first symptom start date? <span style={{ color: 'red' }}>*</span>
                  {getSaveIndicator('ChronicConditionStartDate')}
                </label>
                <input
                  type="date"
                  id="chronicConditionStartDate"
                  value={chronicConditionStartDate}
                  onChange={(e) => handleDateChange('ChronicConditionStartDate', e.target.value, setChronicConditionStartDate)}
                  style={{ width: '250px' }}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="improvement">
                Improvement: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('Improvement')}
              </label>
              <select
                id="improvement"
                value={improvement}
                onChange={(e) => handleSelectChange('Improvement', e.target.value, setImprovement)}
                style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
              >
                <option value="">Select</option>
                {improvementOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="patientPresentation">
                Patient Presentation: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('PatientPresentation')}
              </label>
              <textarea
                id="patientPresentation"
                value={patientPresentation}
                onChange={(e) => handleFieldChange('PatientPresentation', e.target.value, setPatientPresentation)}
                onBlur={() => handleFieldBlur('PatientPresentation', patientPresentation)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>
          </>
        )}

        {reportRecommendation === 'T' && (
          <div className="form-group">
            <label htmlFor="treatmentPlanRecommended">
              Treatment plan {isInitial ? 'recommended' : ''}: <span style={{ color: 'red' }}>*</span>
              {getSaveIndicator('TreatmentPlanRecommended')}
            </label>
            <textarea
              id="treatmentPlanRecommended"
              value={treatmentPlanRecommended}
              onChange={(e) => handleFieldChange('TreatmentPlanRecommended', e.target.value, setTreatmentPlanRecommended)}
              onBlur={() => handleFieldBlur('TreatmentPlanRecommended', treatmentPlanRecommended)}
              rows="5"
              style={{ width: '100%' }}
            />
          </div>
        )}

        {isFollowUp && (
          <>
            <div className="form-group">
              <label htmlFor="therapySessionsSummary">
                What has been completed with patient in therapy to date: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('TherapySessionsSummary')}
              </label>
              <textarea
                id="therapySessionsSummary"
                value={therapySessionsSummary}
                onChange={(e) => handleFieldChange('TherapySessionsSummary', e.target.value, setTherapySessionsSummary)}
                onBlur={() => handleFieldBlur('TherapySessionsSummary', therapySessionsSummary)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="furtherSessionReasons">
                What still needs to be covered and why: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('FurtherSessionReasons')}
              </label>
              <textarea
                id="furtherSessionReasons"
                value={furtherSessionReasons}
                onChange={(e) => handleFieldChange('FurtherSessionReasons', e.target.value, setFurtherSessionReasons)}
                onBlur={() => handleFieldBlur('FurtherSessionReasons', furtherSessionReasons)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="riskAssessment">
                Risk Assessment: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('RiskAssessment')}
              </label>
              <textarea
                id="riskAssessment"
                value={riskAssessment}
                onChange={(e) => handleFieldChange('RiskAssessment', e.target.value, setRiskAssessment)}
                onBlur={() => handleFieldBlur('RiskAssessment', riskAssessment)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="rationaleForExtension">
                Rationale for requesting extension: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('RationaleForExtension')}
              </label>
              <textarea
                id="rationaleForExtension"
                value={rationaleForExtension}
                onChange={(e) => handleFieldChange('RationaleForExtension', e.target.value, setRationaleForExtension)}
                onBlur={() => handleFieldBlur('RationaleForExtension', rationaleForExtension)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>
          </>
        )}

        {reportRecommendation === 'T' && (
          <>
            <div className="form-group">
              <label className="inline-label">
                <strong>Treatment pathway: <span style={{ color: 'red' }}>*</span></strong>
                {getSaveIndicator('TreatmentPathway')}
              </label>
              <div className="radio-group">
                {treatmentPathwayOptions.map(option => (
                  <label key={option.value} style={{ display: 'block', marginBottom: '8px' }}>
                    <input
                      type="radio"
                      name="treatmentPathway"
                      value={option.value}
                      checked={treatmentPathway === option.value}
                      onChange={() => handleSelectChange('TreatmentPathway', option.value, setTreatmentPathway)}
                      style={{ marginRight: '8px' }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="recommendedNumberOfSessions">
                Recommended number of additional sessions: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('RecommendedNumberOfSessions')}
              </label>
              <input
                type="number"
                id="recommendedNumberOfSessions"
                value={recommendedNumberOfSessions}
                onChange={(e) => handleNumberChange('RecommendedNumberOfSessions', e.target.value, setRecommendedNumberOfSessions)}
                style={{ width: '150px', padding: '8px' }}
                min="0"
              />
            </div>

            {isFollowUp && (
              <div className="form-group">
                <label className="inline-label">
                  <strong>Will the recommended sessions enable recovery? <span style={{ color: 'red' }}>*</span></strong>
                  {getSaveIndicator('WillRecommendedSessionsEnableRecovery')}
                </label>
                <div className="radio-group-inline">
                  <label>
                    <input
                      type="radio"
                      name="willRecommendedSessionsEnableRecovery"
                      checked={willRecommendedSessionsEnableRecovery === true}
                      onChange={() => handleBooleanChange('WillRecommendedSessionsEnableRecovery', true, setWillRecommendedSessionsEnableRecovery)}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="willRecommendedSessionsEnableRecovery"
                      checked={willRecommendedSessionsEnableRecovery === false}
                      onChange={() => handleBooleanChange('WillRecommendedSessionsEnableRecovery', false, setWillRecommendedSessionsEnableRecovery)}
                    />
                    No
                  </label>
                </div>
              </div>
            )}
          </>
        )}

        {reportRecommendation === 'D' && (
          <>
            <div className="form-group">
              <label htmlFor="dischargeReasonID">
                Discharge Reason: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('DischargeReasonID')}
              </label>
              <select
                id="dischargeReasonID"
                value={dischargeReasonID}
                onChange={(e) => handleSelectChange('DischargeReasonID', e.target.value, setDischargeReasonID)}
                style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
              >
                <option value="">Select</option>
                {dischargeReasonOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="furtherDetails">
                Please provide further details about why treatment is not recommended at this time, including any immediate recommendations: <span style={{ color: 'red' }}>*</span>
                {getSaveIndicator('FurtherDetails')}
              </label>
              <textarea
                id="furtherDetails"
                value={furtherDetails}
                onChange={(e) => handleFieldChange('FurtherDetails', e.target.value, setFurtherDetails)}
                onBlur={() => handleFieldBlur('FurtherDetails', furtherDetails)}
                rows="4"
                style={{ width: '100%' }}
              />
            </div>

            <div className="form-group">
              <label className="inline-label">
                <strong>Further recommendations: {isInitial && <span style={{ color: 'red' }}>*</span>}</strong>
                {getSaveIndicator('Recommendation1')}
              </label>
              <div className="radio-group">
                {recommendation1Options.map(option => (
                  <label key={option.value} style={{ display: 'block', marginBottom: '8px' }}>
                    <input
                      type="radio"
                      name="recommendation1"
                      value={option.value}
                      checked={recommendation1 === option.value}
                      onChange={() => handleSelectChange('Recommendation1', option.value, setRecommendation1)}
                      style={{ marginRight: '8px' }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      </fieldset>

    </div>
  );
};

export default TreatmentRecommendationsEformTab;
